import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import DataTable from 'src/components/ui/data-table';
import AppSelect from 'src/components/common/AppSelect';
import { Button } from 'src/components/ui/button';
import { useExternalCandidates } from 'src/services/api/queries/candidateQueries';
import { Users } from 'lucide-react';

const ExternalCandidatesModal = ({ isOpen, onClose, assignmentId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedSource, setSelectedSource] = useState('Candidate'); // Default to "Candidate"

  // Map display value to API value: "Candidate" -> "Direct", "Partner" -> "Partner"
  const sourceApiValue = selectedSource === 'Candidate' ? 'Direct' : 'Partner';

  // Only fetch when modal is open and assignmentId is available
  const shouldFetch = isOpen === true && Boolean(assignmentId);

  const { data: externalCandidatesData, isLoading } = useExternalCandidates({
    taLeadAssignmentId: assignmentId,
    page: currentPage,
    pageSize,
    source: sourceApiValue,
    enabled: shouldFetch,
  });

  // Reset pagination when modal opens or source changes
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setPageSize(10);
    }
  }, [isOpen]);

  // Reset to page 1 when source changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSource]);

  const candidates = externalCandidatesData?.data?.items || [];
  const pagination = externalCandidatesData?.data?.pagination || {};

  const columns = [
    {
      key: 'candidate_name',
      header: 'Name',
      className: 'text-sm',
      tooltip: true,
      render: (_value, row) => row.candidate_name || '-',
    },
    {
      key: 'candidate_email',
      header: 'Email',
      className: 'text-sm',
      tooltip: true,
      render: (_value, row) => row.candidate_email || '-',
    },
    {
      key: 'candidate_phone',
      header: 'Phone',
      className: 'text-sm',
      render: (_value, row) => row.candidate_phone || '-',
    },
    {
      key: 'location_name',
      header: 'Location',
      className: 'text-sm',
      render: (_value, row) => row.location_name || '-',
    },
    {
      key: 'partner_name',
      header: 'Partner / Institution',
      className: 'text-sm',
      render: (_value, row) => row.partner_name || '-',
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>External Candidates</DialogTitle>
          <DialogDescription>
            Candidates submitted through external sources for this assignment.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4 max-h-[70vh]">
          <div className="flex items-center gap-2">
            <AppSelect
              options={[
                { value: 'Candidate', label: 'Candidate' },
                { value: 'Partner', label: 'Partner' },
              ]}
              value={selectedSource}
              onValueChange={setSelectedSource}
              id="source-select"
              placeholder="Select source"
              className="w-40"
            />
          </div>
          <DataTable
            className="border-border"
            data={candidates}
            columns={columns}
            rowKey="candidate_id"
            hover
            stickyHeader
            loading={isLoading}
            emptyState={
              <div className="flex flex-col items-center justify-center h-full border border-border rounded-lg p-4 gap-2 bg-card min-h-[300px]">
                <Users className="w-6 h-6 text-muted-foreground font-normal" />
                <p className="text-muted-foreground">
                  No external candidates found
                </p>
              </div>
            }
            pagination
            currentPage={currentPage}
            totalPages={pagination.total_pages || 0}
            pageSize={pageSize}
            totalItems={pagination.total || 0}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => {
              setPageSize(parseInt(newSize, 10));
              setCurrentPage(1);
            }}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExternalCandidatesModal;
